// Presentation helpers only. Reel outcomes and prize amounts are supplied by
// pb-worker; randomness here is limited to idle filler art and animation timing.

export const SLOT_SYMBOLS = [
    "swimming-trunks",
    "coconut-drink",
    "sunglasses",
    "beach-ball",
    "starfish",
    "shell",
    "golden-beach-coins",
    "energy-drink",
    "golden-shell",
    "thief",
    "friend-steal",
] as const;

export type SlotSymbol = (typeof SLOT_SYMBOLS)[number];

export const JACKPOT_SYMBOL: SlotSymbol = "golden-shell";

const FILLER_POOL: SlotSymbol[] = [
    "swimming-trunks",
    "coconut-drink",
    "sunglasses",
    "beach-ball",
    "starfish",
    "shell",
];

export const REEL_STOP_DELAYS_MS = [50, 100, 200] as const;
export const REEL_STOP_DURATIONS_MS = [150, 200, 600] as const;
export const SPIN_RESOLVE_DELAY_MS = 400;
export const JACKPOT_HOLD_MS = 1000;

export type PrizeCurrency =
    | "beach-coins"
    | "energy-drinks"
    | "usdt"
    | "notcoin"
    | "gram"
    | "friend-steal";

export interface SpinPrize {
    currency: PrizeCurrency | null;
    amount: number;
}

export interface SpinResult {
    outcomeId: string;
    reels: [SlotSymbol, SlotSymbol, SlotSymbol];
    prize: SpinPrize;
}

export function getRandom(min: number, max: number): number {
    return min + Math.random() * (max - min);
}

function getRandomItem<T>(pool: T[], exclude: T[] = []): T {
    const candidates = pool.filter((item) => !exclude.includes(item));
    return candidates[Math.floor(Math.random() * candidates.length)]!;
}

export interface ReelDisplay {
    symbol: SlotSymbol;
    above: SlotSymbol;
    below: SlotSymbol;
    animationDelayMs: number;
}

export function buildReelDisplays(
    result?: SpinResult,
    spinning = false,
): ReelDisplay[] {
    const displays: ReelDisplay[] = [];
    const aboveUsed: SlotSymbol[] = [];
    const belowUsed: SlotSymbol[] = [];

    for (let index = 0; index < 3; index += 1) {
        const idleExclude =
            !result &&
            index === 2 &&
            displays[0]!.symbol === displays[1]!.symbol
                ? [displays[0]!.symbol]
                : [];
        const symbol =
            result?.reels[index] ?? getRandomItem(FILLER_POOL, idleExclude);
        const above = getRandomItem(FILLER_POOL, [...aboveUsed, symbol]);
        const below = getRandomItem(FILLER_POOL, [...belowUsed, symbol, above]);
        if (index === 0) {
            aboveUsed.push(above);
            belowUsed.push(below);
        }
        displays.push({
            symbol,
            above,
            below,
            animationDelayMs: spinning ? 50 * index : 0,
        });
    }
    return displays;
}

export function formatCompact(value: number, locale = "en"): string {
    return new Intl.NumberFormat(locale, {
        notation: "compact",
        maximumFractionDigits: 2,
    }).format(value);
}
